import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  StartOnboardingResponse,
  SaveStepResponse,
  SaveBusinessTypeRequest,
  SavePlaceDetailsRequest,
  SaveEmailRequest,
  SaveContactRequest,
  SaveLanguagesRequest,
  SaveBookingSystemRequest,
  SaveBillingRequest,
  PlacesAutocompleteResponse,
  PlaceDetails,
  SendCodeResponse,
  VerifyCodeResponse,
  BookingDetectionResult,
  ValidateBookingResponse,
  CreatePaymentResponse,
  PaymentContextResponse,
  CheckEmailResponse,
  OpenTablePartnerIdResponse,
  Lead,
  BookingSystem,
} from '@/types/onboarding'

const BASE_URL = '/public/onboarding'

// Start a new onboarding flow. Accepts an optional Turnstile token so the
// backend can verify the request is human before creating a lead (bot/junk
// signup vector). The token rides along even before backend verification lands.
export function useStartLeadOnboarding() {
  return useMutation({
    mutationFn: async (
      turnstileToken?: string
    ): Promise<StartOnboardingResponse> => {
      const response = await api.post(
        `${BASE_URL}/start`,
        turnstileToken ? { turnstile_token: turnstileToken } : {}
      )
      return response.data
    },
  })
}

// --- Domain-specific step save hooks ---

export function useSaveBusinessType() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SaveBusinessTypeRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/business-type`, data)
      return response.data
    },
  })
}

export function useSavePlaceDetails() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SavePlaceDetailsRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/place-details`, data)
      return response.data
    },
  })
}

export function useSaveEmail() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SaveEmailRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/email`, data)
      return response.data
    },
  })
}

export function useSaveContact() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SaveContactRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/contact`, data)
      return response.data
    },
  })
}

export function useSaveLanguages() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SaveLanguagesRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/languages`, data)
      return response.data
    },
  })
}

export function useSaveBookingSystem() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SaveBookingSystemRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/booking-system`, data)
      return response.data
    },
  })
}

export function useSaveBilling() {
  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string
      data: SaveBillingRequest
    }): Promise<SaveStepResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/billing`, data)
      return response.data
    },
  })
}

// Places autocomplete search
export function useLeadPlacesAutocomplete(query: string, leadId: string | null, enabled = false) {
  return useQuery({
    queryKey: ['lead-places-autocomplete', leadId, query],
    queryFn: async (): Promise<PlacesAutocompleteResponse> => {
      const response = await api.get(`${BASE_URL}/places/autocomplete`, {
        params: { q: query, lead_id: leadId },
      })
      return response.data
    },
    enabled: enabled && query.length >= 3 && !!leadId,
    staleTime: 30000, // Cache for 30 seconds
  })
}

// Get full place details (phone, website, etc.)
export function useLeadPlaceDetails(placeId: string | null, leadId: string | null, enabled = false) {
  return useQuery({
    queryKey: ['lead-place-details', leadId, placeId],
    queryFn: async (): Promise<PlaceDetails> => {
      const response = await api.get(`${BASE_URL}/places/details/${placeId}`, {
        params: { lead_id: leadId },
      })
      return response.data
    },
    enabled: enabled && !!placeId && !!leadId,
    staleTime: 60000, // Cache for 1 minute
  })
}

// Send phone verification code. Accepts an optional Turnstile token so the
// backend can verify the request before sending an SMS (the paid Twilio vector).
export function useSendLeadPhoneCode() {
  return useMutation({
    mutationFn: async ({
      leadId,
      phoneNumber,
      turnstileToken,
    }: {
      leadId: string
      phoneNumber: string
      turnstileToken?: string
    }): Promise<SendCodeResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/phone/send-code`, {
        phone_number: phoneNumber,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      })
      return response.data
    },
  })
}

// Verify phone code
export function useVerifyLeadPhoneCode() {
  return useMutation({
    mutationFn: async ({
      leadId,
      code,
    }: {
      leadId: string
      code: string
    }): Promise<VerifyCodeResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/phone/verify`, { code })
      return response.data
    },
  })
}

const VALID_DETECTION_STATUSES = new Set<string>(['PENDING', 'COMPLETED', 'NOT_STARTED', 'FAILED'])

// Poll booking detection via lead status (detection data is in the lead response)
export function useLeadBookingDetection(leadId: string | null, enabled = false) {
  return useQuery({
    queryKey: ['lead-booking-detection', leadId],
    queryFn: async (): Promise<BookingDetectionResult> => {
      const response = await api.get(`${BASE_URL}/${leadId}`)
      const lead = response.data as Lead
      const rawStatus = lead.data.booking_detection_status
      return {
        status: (rawStatus && VALID_DETECTION_STATUSES.has(rawStatus) ? rawStatus : 'NOT_STARTED') as BookingDetectionResult['status'],
        detected_system: (lead.data.detected_booking_system as BookingDetectionResult['detected_system']) || null,
        restaurant_id: lead.data.detected_restaurant_id || null,
      }
    },
    enabled: enabled && !!leadId,
    refetchInterval: (query) => {
      // Poll every 3 seconds until completed
      const data = query.state.data
      if (data?.status === 'COMPLETED' || data?.status === 'FAILED') {
        return false
      }
      return 3000
    },
  })
}

// Validate booking system credentials
export function useValidateLeadBookingSystem() {
  return useMutation({
    mutationFn: async ({
      leadId,
      bookingSystem,
      restaurantId,
      apiKey,
    }: {
      leadId: string
      bookingSystem: BookingSystem
      restaurantId?: string
      apiKey?: string
    }): Promise<ValidateBookingResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/validate-booking-system`, {
        booking_system: bookingSystem,
        restaurant_id: restaurantId,
        api_key: apiKey,
      })
      return response.data
    },
  })
}

// Create payment intent (lead-based, existing flow)
export function useCreateLeadPayment() {
  return useMutation({
    mutationFn: async (leadId: string): Promise<CreatePaymentResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/payment`)
      return response.data
    },
  })
}

// Unified payment context — accepts lead_id, assistant_id, or neither
export function useCreatePaymentContext() {
  return useMutation({
    mutationFn: async (params: { lead_id?: string; assistant_id?: number } = {}): Promise<PaymentContextResponse> => {
      const response = await api.post('/public/onboarding/payment', params)
      return response.data
    },
  })
}

// Get lead status (for resuming)
export function useLeadStatus(leadId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['lead-status', leadId],
    queryFn: async (): Promise<Lead> => {
      const response = await api.get(`${BASE_URL}/${leadId}`)
      return response.data
    },
    enabled: enabled && !!leadId,
  })
}

// Check if email exists
export function useCheckLeadEmail() {
  return useMutation({
    mutationFn: async (email: string): Promise<CheckEmailResponse> => {
      const response = await api.get(`${BASE_URL}/check-email`, {
        params: { email },
      })
      return response.data
    },
  })
}

// Resume onboarding by email
export function useResumeLeadOnboarding() {
  return useMutation({
    mutationFn: async (email: string): Promise<Lead> => {
      const response = await api.get(`${BASE_URL}/resume`, {
        params: { email },
      })
      return response.data
    },
  })
}

// Generate or get OpenTable partner ID
export function useGenerateOpenTablePartnerId() {
  return useMutation({
    mutationFn: async (leadId: string): Promise<OpenTablePartnerIdResponse> => {
      const response = await api.post(`${BASE_URL}/${leadId}/opentable-partner-id`)
      return response.data
    },
  })
}
